import React, { useCallback } from "react";
import { BillStatus } from "@common/types/bill";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useCreateBills } from "@api/bill.api";
import { AccrualImporterDrawer } from "@components/accrualImporter/AccrualImporterDrawer";
import { AccrualImporterGroup } from "@components/accrualImporter/useAccrualImporterState";
import { mapGroupToAccrualItem } from "@components/accrualImporter/mapGroupsToAccrualItems";
import { AccrualImporterRow } from "@components/accrualImporter/types";

interface Props {
    open?: boolean;
    onClose: () => void;
    initialRows?: AccrualImporterRow[];
}

export const ManualBillCreationDrawer: React.FC<Props> = ({
    open,
    onClose,
    initialRows,
}) => {
    const { workspaceId } = useScope();
    const createBills = useCreateBills();
    const { toast, error } = useToaster();

    const handleSubmit = useCallback(
        async (groups: AccrualImporterGroup[]) => {
            if (!workspaceId) {
                return;
            }

            try {
                await createBills.mutateAsync({
                    workspaceId,
                    bills: groups.map((group) => {
                        const item = mapGroupToAccrualItem(group);
                        return {
                            ...item,
                            status: BillStatus.OPEN,
                            lines: item.lines.map(
                                ({ accrualAccountId, ...line }) => ({
                                    ...line,
                                    accountsPayableId: accrualAccountId,
                                }),
                            ),
                        };
                    }),
                });
                toast("Bills imported");
                onClose();
            } catch {
                error("Failed to import bills");
            }
        },
        [workspaceId, createBills, toast, error, onClose],
    );

    return (
        <AccrualImporterDrawer
            mode="bill"
            open={open}
            onClose={onClose}
            onSubmit={handleSubmit}
            isSubmitting={createBills.isPending}
            initialRows={initialRows}
        />
    );
};
